import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, ShieldCheck, XCircle, CheckCircle2, Loader2, RotateCw, Send } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Btn, Loading, Empty, useCrud, handleErr, fmtDateTime } from "./AdminUI";

const STATUS_CLS = {
  paid: "text-[#22C55E]", failed: "text-red-400", cancelled: "text-red-400",
  expired: "text-orange-400", pending: "text-[#2563EB]", processing: "text-[#2563EB]",
};

function Attention() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  const load = () => { setLoading(true); endpoints.adminPaymentsAttention().then((r) => setItems(r.data?.items || [])).catch(handleErr).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await endpoints.adminReconcilePayments();
      if (data.recovered > 0) toast.success(`Recovered ${data.recovered} missed payment(s) · checked ${data.checked}`);
      else toast.message(`Checked ${data.checked} MoMo payment(s) — none needed recovery`);
      load();
    } catch (e) { handleErr(e); } finally { setReconciling(false); }
  };

  const verify = async (p) => {
    setBusy(p.id);
    try { const { data } = await endpoints.adminVerifyPayment(p.id);
      const r = data.result;
      if (r === "paid") toast.success("Confirmed PAID with provider & fulfilled ✅");
      else if (r === "failed" || r === "expired") toast.message(`Provider says: ${r}`);
      else if (r === "pending") toast.message("Provider still shows pending");
      else toast.message("Provider status unknown — no reachable lookup");
      load();
    } catch (e) { handleErr(e); } finally { setBusy(null); }
  };
  const markPaid = async (p) => {
    const reference = window.prompt(`MANUAL FULFILMENT for ${p.userPhone || p.userId}\n\nOnly do this if you have CONFIRMED the money arrived.\nEnter the provider transaction reference:`);
    if (!reference) return;
    const reason = window.prompt("Reason / note for the audit log:");
    if (!reason) return;
    setBusy(p.id);
    try { await endpoints.adminMarkPaid(p.id, { reference, reason }); toast.success("Marked paid & fulfilled (audited)"); load(); }
    catch (e) { handleErr(e); } finally { setBusy(null); }
  };
  const cancel = async (p) => {
    const reason = window.prompt("Cancel this transaction — reason:");
    if (reason === null) return;
    setBusy(p.id);
    try { await endpoints.adminCancelPayment(p.id, { reason }); toast.success("Cancelled"); load(); }
    catch (e) { handleErr(e); } finally { setBusy(null); }
  };

  if (loading) return <Card className="p-4 mb-6"><Loader2 className="w-5 h-5 animate-spin text-[#E11D2A]" /></Card>;

  return (
    <Card className="mb-6 border-amber-500/30" data-testid="attention-panel">
      <div className="p-4 flex items-center justify-between border-b border-[#273042]">
        <div className="flex items-center gap-2 text-white font-bold"><AlertTriangle className={`w-5 h-5 ${items.length ? "text-amber-400" : "text-slate-500"}`} /> Needs attention {items.length > 0 && <span className="min-w-6 h-6 px-2 rounded-full bg-amber-500 text-black text-xs font-extrabold flex items-center justify-center">{items.length}</span>}</div>
        <div className="flex items-center gap-2">
          <Btn onClick={reconcile} disabled={reconciling} data-testid="reconcile-btn">{reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />} Reconcile last 24h</Btn>
          <Btn variant="ghost" onClick={load} className="h-9"><RefreshCw className="w-4 h-4" /> Refresh</Btn>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-400" /> No stuck or unfulfilled payments. All clear.</div>
      ) : (
        <div className="divide-y divide-[#273042]">
          {items.map((p) => (
            <div key={p.id} data-testid={`attention-${p.id}`} className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${p.issue === "paid_unfulfilled" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{p.issue === "paid_unfulfilled" ? "PAID · NOT FULFILLED" : "STUCK PENDING"}</span>
                  <span className="text-xs text-slate-400 uppercase">{p.method} · {p.purpose || p.planLabel || "subscription"}</span>
                </div>
                <div className="text-white font-semibold mt-1">{Number(p.amount || 0).toLocaleString()} {p.currency} — {p.userPhone || p.momoPhone || p.userId}</div>
                <div className="text-xs text-slate-500">ref {p.reference || p.id}{p.providerRef ? ` · BeSoft ${p.providerRef}` : ""} · {fmtDateTime(p.createdAt)}</div>
              </div>
              <div className="flex items-center gap-2">
                <Btn onClick={() => verify(p)} disabled={busy === p.id} data-testid={`verify-${p.id}`}>{busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verify now</Btn>
                <Btn variant="ghost" onClick={() => markPaid(p)} disabled={busy === p.id} data-testid={`markpaid-${p.id}`}><CheckCircle2 className="w-4 h-4" /> Mark paid & fulfil</Btn>
                {p.issue !== "paid_unfulfilled" && <Btn variant="danger" onClick={() => cancel(p)} disabled={busy === p.id} data-testid={`cancel-${p.id}`}><XCircle className="w-4 h-4" /> Cancel</Btn>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

const isFailed = (p) => ["failed", "expired", "cancelled"].includes(p.status);

const RetryMsgBtn = ({ p, onDone }) => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await endpoints.adminPaymentRetryMsg(p.id);
      if (data.result === "sent") toast.success("Try-again message sent (SMS + WhatsApp)");
      else if (data.result === "skipped") toast.warning(data.message || "Skipped", { duration: 7000 });
      else toast.error("Message could not be delivered");
      onDone && onDone();
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  return (
    <button onClick={send} disabled={busy} data-testid={`retry-msg-${p.id}`}
      className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#E11D2A]/15 border border-[#E11D2A]/40 text-[#FCA5A5] text-xs font-bold hover:bg-[#E11D2A]/25 disabled:opacity-60 transition-colors">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
      {p.retryMsgSentAt ? "Send try-again message again" : "Send try-again message"}
    </button>
  );
};

export default function AdminPayments() {
  const { data: payments, loading, reload } = useCrud(endpoints.adminPayments);
  const [bulk, setBulk] = useState(false);
  const total = payments.filter((p) => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0);
  const failedCount = payments.filter((p) => p.status === "failed" || p.status === "expired").length;

  const messageAll = async () => {
    if (!window.confirm("Send a 'payment not successful — please try again' message to every customer whose payment failed in the last 7 days?\n\n• One message per phone number (repeat attempts are merged)\n• Numbers messaged in the last 30 minutes are skipped\n• Customers who paid successfully (last 24h or after the failure) are skipped")) return;
    setBulk(true);
    try {
      const { data } = await endpoints.adminPaymentRetryMsgAll(7);
      toast.success(`Sent to ${data.sent} customer(s) · skipped ${data.skipped} (${data.recently_sent} messaged < 30 min ago, ${data.already_paid} already paid) · ${data.duplicates} repeat attempts merged · failed ${data.failed}`, { duration: 8000 });
      reload && reload();
    } catch (e) { handleErr(e); } finally { setBulk(false); }
  };

  return (
    <div>
      <PageHeader title="Payments" desc={`${payments.length} transaction(s) · ${total.toLocaleString()} RWF collected`}>
        <Btn onClick={messageAll} disabled={bulk || failedCount === 0} data-testid="retry-msg-all-btn">
          {bulk ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Message all failed customers (last 7 days)
        </Btn>
      </PageHeader>
      <Attention />
      {loading ? <Loading /> : payments.length === 0 ? <Empty text="No payments yet." /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead><tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-[#273042]">
              <th className="p-4">Customer</th><th className="p-4">Plan</th><th className="p-4">Method</th><th className="p-4">Amount</th><th className="p-4">Status / details</th><th className="p-4">Reference</th><th className="p-4">Date &amp; time</th>
            </tr></thead>
            <tbody className="divide-y divide-[#273042]">
              {payments.map((p) => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                  <td className="p-4 text-white">
                    <div className="font-semibold">{p.userName || "—"}</div>
                    <div className="text-xs text-slate-400">{p.userPhone || p.momoPhone || p.userEmail || p.userId}</div>
                  </td>
                  <td className="p-4 text-slate-300">{p.planLabel || p.plan}</td>
                  <td className="p-4 text-slate-300 capitalize">{p.method}</td>
                  <td className="p-4 text-white font-semibold">{Number(p.amount).toLocaleString()} {p.currency}</td>
                  <td className="p-4">
                    <span className={`text-xs font-bold uppercase ${STATUS_CLS[p.status] || "text-[#2563EB]"}`}>{p.status}{p.manualPaid ? " · manual" : ""}</span>
                    {(p.status === "failed" || p.status === "expired") && (
                      <div className="mt-1 text-[11px] text-amber-400/90 max-w-[220px]" data-testid={`payment-failure-${p.id}`}>
                        {p.failureReason || "Payment failed – no additional failure details were provided by the payment provider."}
                        {p.providerRaw && <span className="block text-slate-500">Provider status: {p.providerRaw}</span>}
                      </div>
                    )}
                    {isFailed(p) && (
                      <div>
                        <RetryMsgBtn p={p} onDone={reload} />
                        {p.retryMsgSentAt && <div className="text-[10px] text-slate-500 mt-1" data-testid={`retry-msg-sent-${p.id}`}>Message {p.retryMsgStatus || "sent"} {fmtDateTime(p.retryMsgSentAt)}</div>}
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-slate-400 text-xs font-mono" data-testid={`payment-ref-${p.id}`}>
                    <div>{p.reference || p.id}</div>
                    {p.providerRef && <div className="text-slate-500" title="Cross-check in the BeSoft merchant portal">BeSoft {p.providerRef}</div>}
                  </td>
                  <td className="p-4 text-slate-400 text-xs" data-testid={`payment-date-${p.id}`}>{fmtDateTime(p.createdAt)}{p.failedAt ? <span className="block text-slate-500">failed {fmtDateTime(p.failedAt)}</span> : ""}{p.paidAt ? <span className="block text-emerald-500/80">paid {fmtDateTime(p.paidAt)}</span> : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
